import { useMemo, useState, type FormEvent } from 'react';
import { Trash2, Plus } from 'lucide-react';

import { useBlacklist, normalizeTag } from './useBlacklist';
import { getAllSources } from '@/sources/registry';
import type { SourceId } from '@/sources/types';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { blacklistKey } from '@/db/schema';

type SourceScope = 'all' | SourceId;

/**
 * Settings page section: add/remove blacklist entries with an optional
 * per-source scope. `all` (the default) maps to `source = null` in the
 * IndexedDB schema, meaning "applies to every source".
 */
export function BlacklistEditor() {
  const { rows, loading, add, remove } = useBlacklist();
  const sources = getAllSources();
  const [draftTag, setDraftTag] = useState('');
  const [scope, setScope] = useState<SourceScope>('all');
  const [busy, setBusy] = useState(false);

  const grouped = useMemo(() => {
    const all = rows.filter((r) => r.source === null);
    const perSource = new Map<SourceId, typeof rows>();
    for (const r of rows) {
      if (r.source === null) continue;
      const arr = perSource.get(r.source as SourceId) ?? [];
      arr.push(r);
      perSource.set(r.source as SourceId, arr);
    }
    return { all, perSource };
  }, [rows]);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (!draftTag.trim()) return;
    setBusy(true);
    try {
      await add(draftTag, scope === 'all' ? null : scope);
      setDraftTag('');
    } finally {
      setBusy(false);
    }
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Tag blacklist</CardTitle>
        <CardDescription>
          Hide galleries that match any of these tags. Use <code>namespace:name</code>{' '}
          (e.g. <code>artist:foo</code>) or a bare tag name. Matching is
          case-insensitive and exact — no fuzzy.
        </CardDescription>
      </CardHeader>
      <CardContent className="flex flex-col gap-4">
        <form
          onSubmit={handleSubmit}
          className="flex flex-col gap-2 sm:flex-row sm:items-end"
        >
          <div className="flex-1">
            <label className="mb-1 block text-xs uppercase tracking-wide text-muted-foreground">
              Tag
            </label>
            <Input
              value={draftTag}
              onChange={(e) => setDraftTag(e.target.value)}
              placeholder="e.g. tag:big breasts or yuri"
              disabled={busy}
            />
          </div>
          <div className="sm:w-44">
            <label className="mb-1 block text-xs uppercase tracking-wide text-muted-foreground">
              Scope
            </label>
            <select
              value={scope}
              onChange={(e) => setScope(e.target.value as SourceScope)}
              className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm"
              disabled={busy}
            >
              <option value="all">All sources</option>
              {sources.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name}
                </option>
              ))}
            </select>
          </div>
          <Button type="submit" disabled={busy || !draftTag.trim()}>
            <Plus className="mr-1 h-4 w-4" />
            Add
          </Button>
        </form>

        {loading ? (
          <p className="text-sm text-muted-foreground">Loading…</p>
        ) : rows.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            No tags blacklisted yet. Add one above to start filtering galleries.
          </p>
        ) : (
          <div className="flex flex-col gap-3">
            {grouped.all.length > 0 && (
              <BlacklistGroup
                title="All sources"
                rows={grouped.all}
                onRemove={(tag, source) => void remove(tag, source)}
              />
            )}
            {[...grouped.perSource.entries()]
              .sort((a, b) => a[0].localeCompare(b[0]))
              .map(([sourceId, sourceRows]) => (
                <BlacklistGroup
                  key={sourceId}
                  title={sources.find((s) => s.id === sourceId)?.name ?? sourceId}
                  rows={sourceRows}
                  onRemove={(tag, source) => void remove(tag, source)}
                />
              ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

interface BlacklistGroupProps {
  title: string;
  rows: Array<{ tag: string; source: string | null }>;
  onRemove: (tag: string, source: string | null) => void;
}

function BlacklistGroup({ title, rows, onRemove }: BlacklistGroupProps) {
  return (
    <div className="flex flex-col gap-1">
      <h4 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
        {title}
      </h4>
      <div className="flex flex-wrap gap-1.5">
        {rows.map((row) => (
          <Badge
            key={blacklistKey(row.source, row.tag)}
            variant="secondary"
            className="gap-1 pr-1"
          >
            <span>{normalizeTag(row.tag)}</span>
            <button
              type="button"
              onClick={() => onRemove(row.tag, row.source)}
              className="rounded-full p-0.5 hover:bg-background/30"
              aria-label={`Remove ${row.tag}`}
            >
              <Trash2 className="h-3 w-3" />
            </button>
          </Badge>
        ))}
      </div>
    </div>
  );
}
